import * as React from "react";
import clsx from "clsx";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useNavigate } from "@tanstack/react-router";
import {
  Award,
  BookText,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  Layers,
  PenLine,
  Plus,
  RotateCcw,
  Save,
  Sparkles,
  Trash2,
  User,
} from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { TabSwitcher } from "../../../ui/components/TabSwitcher";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import {
  UnsavedChangesChip,
  scrollToFirstUnsavedInSection,
} from "../../../ui/components/UnsavedChangesChip";
import { Button } from "../../../ui/components/Button";
import type { FloatingAction } from "../../../ui/components/FloatingActionBar";
import type { ContextAction } from "../../../ui/components/ContextActions";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useAuth } from "../../lib/auth/hooks";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import {
  LOCALIZATION_DEFINITIONS_COLLAPSE_SCOPE,
  useSectionCollapse,
} from "../../hooks/useSectionCollapse";
import { useI18n } from "../../lib/i18n";
import * as localizationApi from "../../lib/api/localization";
import type {
  Language,
  LocalizationContext,
} from "../../lib/api/localization";
import { AiFillAssistModal } from "./AiFillAssistModal";
import type { AiFillEmptyField } from "./aiFillAssist";
import {
  DEFINITION_CONTEXTS,
  buildDefinitionsFromEntries,
  countAllDirty,
  countModuleDirty,
  emptyModuleDraft,
  entriesFromDefinitions,
  findDuplicateKeys,
  isEntryDirty,
  newDraftEntry,
  sectionIdForContext,
  type DefinitionsDraftState,
  type DraftEntry,
  type ModuleDraft,
} from "./localizationDefinitionsDraft";
import * as s from "./LocalizationDefinitionsPage.css";
import { vars } from "../../../ui/styles/theme.css";
import {
  searchWrap,
  searchIcon,
  searchInput,
  searchClearBtn,
} from "../../../ui/components/Table/Table.css";

function contextIcon(context: LocalizationContext, size: number): React.ReactNode {
  switch (context) {
    case "shared":
      return <Layers size={size} />;
    case "pro":
      return <Briefcase size={size} />;
    case "certification":
      return <Award size={size} />;
    case "account":
      return <User size={size} />;
    default:
      return <BookText size={size} />;
  }
}

function contextForKey(key: string): LocalizationContext | null {
  const head = key.split(".")[0];
  return (DEFINITION_CONTEXTS as readonly string[]).includes(head)
    ? (head as LocalizationContext)
    : null;
}

/** Keys present on the source (default) language but missing from the target draft. */
function collectMissingKeys(
  sourceDraft: Partial<Record<LocalizationContext, ModuleDraft>> | undefined,
  targetDraft: Partial<Record<LocalizationContext, ModuleDraft>> | undefined,
): Array<{ context: LocalizationContext; key: string; sourceText: string }> {
  const missing: Array<{
    context: LocalizationContext;
    key: string;
    sourceText: string;
  }> = [];
  for (const ctx of DEFINITION_CONTEXTS) {
    const source = sourceDraft?.[ctx];
    const target = targetDraft?.[ctx];
    if (!source?.loaded || !target?.loaded) continue;
    const targetKeys = new Set(
      target.entries
        .filter((e) => !e.deleted && e.key.trim())
        .map((e) => e.key.trim()),
    );
    for (const entry of source.entries) {
      if (entry.deleted) continue;
      const key = entry.key.trim();
      if (!key || targetKeys.has(key)) continue;
      missing.push({ context: ctx, key, sourceText: entry.value });
    }
  }
  return missing;
}

/**
 * Catalog t() keys that are not yet present on the default-language draft
 * (health report "untranslated" for the source language).
 */
function collectUntranslatedCatalogKeys(
  usedTKeys: string[],
  defaultDraft: Partial<Record<LocalizationContext, ModuleDraft>> | undefined,
  defaultMessages: Record<string, string>,
): Array<{ context: LocalizationContext; key: string; value: string }> {
  if (!defaultDraft || usedTKeys.length === 0) return [];
  const existingByContext = new Map<LocalizationContext, Set<string>>();
  let anyLoaded = false;
  for (const ctx of DEFINITION_CONTEXTS) {
    const mod = defaultDraft[ctx];
    if (!mod?.loaded) continue;
    anyLoaded = true;
    existingByContext.set(
      ctx,
      new Set(
        mod.entries
          .filter((e) => !e.deleted && e.key.trim())
          .map((e) => e.key.trim()),
      ),
    );
  }
  if (!anyLoaded) return [];

  const out: Array<{ context: LocalizationContext; key: string; value: string }> =
    [];
  const seen = new Set<string>();
  for (const raw of usedTKeys) {
    const key = raw.trim();
    if (!key || seen.has(key)) continue;
    const ctx = contextForKey(key);
    if (!ctx) continue;
    const existing = existingByContext.get(ctx);
    if (!existing || existing.has(key)) continue;
    seen.add(key);
    out.push({
      context: ctx,
      key,
      value: defaultMessages[key] ?? "",
    });
  }
  return out;
}

function StatusIcon({
  status,
  flagged,
}: {
  status: "synced" | "unsaved";
  flagged?: "deletion" | "creation";
}) {
  const { t } = useI18n();
  const [hovered, setHovered] = React.useState(false);
  const label =
    status === "synced"
      ? t("pro.system.localization.definitions.status.synced")
      : flagged === "deletion"
        ? t("pro.system.localization.definitions.status.unsavedDelete")
        : flagged === "creation"
          ? t("pro.system.localization.definitions.status.unsavedCreate")
          : t("pro.system.localization.definitions.status.unsaved");
  return (
    <div
      className={s.statusIconWrap}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={clsx(
          s.statusIcon,
          status === "synced" ? s.statusSynced : s.statusUnsaved,
        )}
      >
        {status === "synced" ? <CheckCircle2 size={14} /> : <PenLine size={14} />}
      </div>
      {hovered && <div className={s.tooltip}>{label}</div>}
    </div>
  );
}

/** Switch to multiline layout past this length, or when the value contains a newline. */
const LONG_DEFINITION_VALUE_CHARS = 80;
/** Commit local key/value edits to parent draft after this idle period. */
const ROW_COMMIT_MS = 200;

function isLongDefinitionValue(value: string): boolean {
  return value.length >= LONG_DEFINITION_VALUE_CHARS || value.includes("\n");
}

function truncateDefinitionPreview(value: string, maxChars = 72): string {
  const oneLine = value.replace(/\s+/g, " ").trim();
  if (oneLine.length <= maxChars) return oneLine;
  return `${oneLine.slice(0, Math.max(0, maxChars - 1))}…`;
}

function isLocallyDirty(
  entry: DraftEntry,
  localKey: string,
  localValue: string,
): boolean {
  if (entry.baselineKey == null) return !entry.deleted;
  if (entry.deleted) return true;
  return localKey !== entry.baselineKey || localValue !== entry.baselineValue;
}

/** Quick filter for the Definitions page's growing per-module string lists. */
type DefinitionsQuickFilter = "all" | "unsaved" | "empty";

function matchesQuickFilter(entry: DraftEntry, filter: DefinitionsQuickFilter): boolean {
  if (filter === "all") return true;
  if (filter === "unsaved") return isLocallyDirty(entry, entry.key, entry.value);
  return entry.value.trim() === "";
}

function matchesSearch(entry: DraftEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    entry.key.toLowerCase().includes(q) || entry.value.toLowerCase().includes(q)
  );
}

type StringRowProps = {
  entry: DraftEntry;
  /** Default-language string for this key, shown when the empty value field is focused. */
  referenceValue?: string;
  onChange: (
    entryId: string,
    patch: Partial<Pick<DraftEntry, "key" | "value">>,
  ) => void;
  onToggleDelete: (entryId: string) => void;
  onReset: (entryId: string) => void;
};

const StringRow = React.memo(function StringRow({
  entry,
  referenceValue,
  onChange,
  onToggleDelete,
  onReset,
}: StringRowProps) {
  const { t } = useI18n();
  const [localKey, setLocalKey] = React.useState(entry.key);
  const [localValue, setLocalValue] = React.useState(entry.value);
  const [valueFocused, setValueFocused] = React.useState(false);
  const [keyFocused, setKeyFocused] = React.useState(false);
  // Long values start collapsed (truncated preview). Crossing the threshold while
  // typing, or clicking the preview, expands the full-width textarea.
  const [multilineExpanded, setMultilineExpanded] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const pendingFocusRef = React.useRef(false);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const localKeyRef = React.useRef(localKey);
  localKeyRef.current = localKey;
  const localValueRef = React.useRef(localValue);
  localValueRef.current = localValue;
  const entryRef = React.useRef(entry);
  entryRef.current = entry;

  const focused = keyFocused || valueFocused;
  const dirty = isLocallyDirty(entry, localKey, localValue);
  const status = dirty ? ("unsaved" as const) : ("synced" as const);
  const isNew = entry.baselineKey == null;
  const disabled = entry.deleted;
  const isLong = isLongDefinitionValue(localValue);
  const showReferenceHint =
    valueFocused && !disabled && !!referenceValue?.trim();

  // Adopt external entry updates when not actively editing (reset, AI fill, etc.).
  React.useEffect(() => {
    if (focused) return;
    setLocalKey(entry.key);
    setLocalValue(entry.value);
  }, [entry.id, entry.key, entry.value, focused]);

  const flush = React.useCallback(() => {
    const current = entryRef.current;
    const patch: Partial<Pick<DraftEntry, "key" | "value">> = {};
    if (localKeyRef.current !== current.key) patch.key = localKeyRef.current;
    if (localValueRef.current !== current.value) {
      patch.value = localValueRef.current;
    }
    if (Object.keys(patch).length === 0) return;
    onChangeRef.current(current.id, patch);
  }, []);

  // Debounced commit while typing; blur/unmount flush immediately.
  React.useEffect(() => {
    if (!focused) return;
    if (localKey === entry.key && localValue === entry.value) return;
    const timer = window.setTimeout(flush, ROW_COMMIT_MS);
    return () => window.clearTimeout(timer);
  }, [localKey, localValue, focused, entry.key, entry.value, flush]);

  React.useEffect(() => () => flush(), [flush]);

  // When value drops below the long threshold, return to the single-line input.
  // Crossing into long is handled in onChange / toggle so we don't fight a user collapse.
  React.useEffect(() => {
    if (!isLong) setMultilineExpanded(false);
  }, [isLong]);

  React.useLayoutEffect(() => {
    if (!pendingFocusRef.current || !multilineExpanded || !isLong) return;
    pendingFocusRef.current = false;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
  }, [multilineExpanded, isLong, localValue]);

  const expandMultiline = (focus = true) => {
    if (focus) pendingFocusRef.current = true;
    setMultilineExpanded(true);
  };

  const toggleMultiline = () => {
    if (disabled) return;
    if (multilineExpanded) {
      setMultilineExpanded(false);
    } else {
      expandMultiline(true);
    }
  };

  const blurValue = () => {
    flush();
    setValueFocused(false);
  };

  const blurKey = () => {
    flush();
    setKeyFocused(false);
  };

  return (
    <div
      className={clsx(
        s.stringRow,
        isNew && !entry.deleted && s.stringRowAdded,
        entry.deleted && s.stringRowDeleted,
      )}
      data-unsaved={dirty ? "true" : undefined}
    >
      <input
        className={clsx(
          s.fieldInput,
          s.keyInput,
          dirty && !entry.deleted && s.fieldInputUnsaved,
        )}
        value={localKey}
        disabled={disabled}
        placeholder={t("pro.system.localization.definitions.row.keyPlaceholder")}
        spellCheck={false}
        onChange={(e) => setLocalKey(e.target.value)}
        onFocus={() => setKeyFocused(true)}
        onBlur={blurKey}
      />
      {isLong ? (
        <button
          type="button"
          className={clsx(
            s.valueSummaryBtn,
            dirty && !entry.deleted && s.fieldInputUnsaved,
          )}
          disabled={disabled}
          title={
            multilineExpanded
              ? t("pro.system.localization.definitions.row.collapseMultiline")
              : t("pro.system.localization.definitions.row.expandMultiline")
          }
          aria-expanded={multilineExpanded}
          onClick={toggleMultiline}
        >
          <span className={s.valueSummaryText}>
            {truncateDefinitionPreview(localValue) ||
              t("pro.system.localization.definitions.row.messageText")}
          </span>
          <ChevronDown
            size={14}
            className={clsx(
              s.valueSummaryIcon,
              multilineExpanded && s.valueSummaryIconExpanded,
            )}
            aria-hidden
          />
        </button>
      ) : (
        <div className={s.valueFieldWrap}>
          <input
            className={clsx(
              s.fieldInput,
              dirty && !entry.deleted && s.fieldInputUnsaved,
            )}
            value={localValue}
            disabled={disabled}
            placeholder={t("pro.system.localization.definitions.row.messageText")}
            onChange={(e) => {
              const next = e.target.value;
              setLocalValue(next);
              if (isLongDefinitionValue(next)) {
                expandMultiline(true);
              }
            }}
            onFocus={() => setValueFocused(true)}
            onBlur={blurValue}
          />
          {showReferenceHint && (
            <div className={s.valueHint} role="tooltip">
              <div className={s.valueHintLabel}>
                {t("pro.system.localization.definitions.row.default")}
              </div>
              <div className={s.valueHintText}>{referenceValue}</div>
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        className={s.resetBtn}
        style={{ visibility: dirty ? "visible" : "hidden" }}
        title={
          isNew
            ? t("pro.system.localization.definitions.row.discardNew")
            : t("pro.system.localization.definitions.row.resetSaved")
        }
        onClick={() => onReset(entry.id)}
      >
        <RotateCcw size={12} />
      </button>
      <button
        type="button"
        className={clsx(s.rowActionBtn, entry.deleted && s.rowActionBtnActive)}
        title={
          entry.deleted
            ? t("pro.system.localization.definitions.row.undoDelete")
            : isNew
              ? t("pro.system.localization.definitions.row.remove")
              : t("shared.common.delete")
        }
        onClick={() => onToggleDelete(entry.id)}
      >
        <Trash2 size={14} />
      </button>
      <StatusIcon
        status={status}
        flagged={
          entry.deleted ? "deletion" : isNew ? "creation" : undefined
        }
      />
      {isLong && multilineExpanded && (
        <div className={s.multilineValueWrap}>
          <textarea
            ref={textareaRef}
            className={clsx(
              s.fieldTextarea,
              dirty && !entry.deleted && s.fieldInputUnsaved,
            )}
            value={localValue}
            disabled={disabled}
            placeholder={t("pro.system.localization.definitions.row.messageText")}
            rows={4}
            onChange={(e) => setLocalValue(e.target.value)}
            onFocus={() => setValueFocused(true)}
            onBlur={blurValue}
          />
          {showReferenceHint && (
            <div className={s.valueHint} role="tooltip">
              <div className={s.valueHintLabel}>
                {t("pro.system.localization.definitions.row.default")}
              </div>
              <div className={s.valueHintText}>{referenceValue}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

type ModuleSectionProps = {
  context: LocalizationContext;
  draft: ModuleDraft;
  referenceValues?: Record<string, string>;
  onUpdateEntry: (
    entryId: string,
    patch: Partial<Pick<DraftEntry, "key" | "value">>,
  ) => void;
  onToggleDelete: (entryId: string) => void;
  onResetEntry: (entryId: string) => void;
  onResetSection: () => void;
  onAddString: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  quickFilter: DefinitionsQuickFilter;
  searchQuery: string;
};

export const ModuleSection = React.memo(function ModuleSection({
  context,
  draft,
  referenceValues,
  onUpdateEntry,
  onToggleDelete,
  onResetEntry,
  onResetSection,
  onAddString,
  expanded,
  onToggleExpanded,
  quickFilter,
  searchQuery,
}: ModuleSectionProps) {
  const { t } = useI18n();
  const sectionId = sectionIdForContext(context);
  const dirtyCount = countModuleDirty(draft);
  const stringCount = draft.entries.filter((e) => !e.deleted).length;
  const resetLabel = t("pro.system.localization.definitions.resetSection");
  const visibleEntries = draft.entries.filter(
    (e) => matchesQuickFilter(e, quickFilter) && matchesSearch(e, searchQuery),
  );

  const scrollBoxRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: visibleEntries.length,
    getScrollElement: () => scrollBoxRef.current,
    estimateSize: () => 44,
    overscan: 10,
  });

  return (
    <CollapsibleSection
      id={sectionId}
      title={context}
      subtitle={
        draft.loaded
          ? t("pro.system.localization.definitions.section.meta", {
              version: draft.version ?? "—",
              count: stringCount,
            })
          : undefined
      }
      icon={contextIcon(context, 20)}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      highlight={dirtyCount > 0}
      mutedHeader
      compactHeader
      flushBody
      unmountCollapsed
      statusIndicators={
        dirtyCount > 0
          ? [
              <Button
                key="reset"
                type="button"
                size="sm"
                color="neutral"
                variant="outline"
                iconOnly
                title={resetLabel}
                aria-label={resetLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  onResetSection();
                }}
              >
                <RotateCcw size={14} />
              </Button>,
              <UnsavedChangesChip
                key="u"
                count={dirtyCount}
                title={t(
                  "pro.system.localization.definitions.scrollToUnsaved",
                )}
                onClick={() =>
                  scrollToFirstUnsavedInSection(sectionId, {
                    expanded,
                    onExpand: onToggleExpanded,
                  })
                }
              />,
            ]
          : undefined
      }
    >
      {draft.loading && (
        <div className={s.emptySection}>{t("shared.common.loading")}</div>
      )}
      {draft.error && (
        <div style={{ padding: 12 }}>
          <Callout variant="error">{draft.error}</Callout>
        </div>
      )}
      {draft.loaded && !draft.loading && draft.entries.length === 0 && (
        <div className={s.emptySection}>
          {t("pro.system.localization.definitions.moduleEmpty")}
        </div>
      )}
      {draft.loaded && draft.entries.length > 0 && visibleEntries.length === 0 && (
        <div className={s.emptySection}>
          {t("pro.system.localization.definitions.quickFilter.noMatches")}
        </div>
      )}
      {draft.loaded && visibleEntries.length > 0 && (
        <div ref={scrollBoxRef} className={s.rowScrollBox}>
          <div
            className={s.rowScrollInner}
            style={{ height: rowVirtualizer.getTotalSize() }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const entry = visibleEntries[virtualRow.index];
              return (
                <div
                  key={entry.id}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  className={s.virtualRow}
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <StringRow
                    entry={entry}
                    referenceValue={referenceValues?.[entry.key.trim()]}
                    onChange={onUpdateEntry}
                    onToggleDelete={onToggleDelete}
                    onReset={onResetEntry}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
      {draft.loaded && (
        <button type="button" className={s.addStringBtn} onClick={onAddString}>
          <Plus size={16} />
          {t("pro.system.localization.definitions.addString")}
        </button>
      )}
    </CollapsibleSection>
  );
});

export function LocalizationDefinitionsPage({
  initialLang,
}: {
  initialLang?: string;
}) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const token = accessToken ?? getAuthCookie("access_token");
  const { confirm, ConfirmModal } = useConfirmModal();

  const [languages, setLanguages] = React.useState<Language[]>([]);
  const [activeLang, setActiveLang] = React.useState<string>(
    () => initialLang?.trim().toLowerCase() || "",
  );
  const [drafts, setDrafts] = React.useState<DefinitionsDraftState>({});
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [aiFillOpen, setAiFillOpen] = React.useState(false);
  const [quickFilter, setQuickFilter] = React.useState<DefinitionsQuickFilter>("all");
  const [catalogUsedTKeys, setCatalogUsedTKeys] = React.useState<string[]>([]);
  const [catalogDefaultMessages, setCatalogDefaultMessages] = React.useState<
    Record<string, string>
  >({});
  const [orphanedCount, setOrphanedCount] = React.useState(0);
  const [removingOrphans, setRemovingOrphans] = React.useState(false);

  const {
    isExpanded: isSectionExpanded,
    toggle: toggleSection,
    expandAll: expandSections,
    bindSectionLayout,
  } = useSectionCollapse(
    LOCALIZATION_DEFINITIONS_COLLAPSE_SCOPE,
  );

  const [searchQuery, setSearchQuery] = React.useState("");
  const deferredSearchQuery = React.useDeferredValue(searchQuery);
  const [searchFocused, setSearchFocused] = React.useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const totalDirty = countAllDirty(drafts);
  const hasDirty = totalDirty > 0;

  const defaultLangCode = React.useMemo(() => {
    return (
      languages.find((l) => l.isDefault)?.code ??
      languages.find((l) => l.code === "en")?.code ??
      ""
    );
  }, [languages]);

  // Load languages
  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    localizationApi
      .fetchAdminLanguages(token)
      .then((rows) => {
        if (cancelled) return;
        const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name));
        setLanguages(sorted);
        setActiveLang((prev) => {
          const fromQuery = initialLang?.trim().toLowerCase();
          if (fromQuery && sorted.some((l) => l.code === fromQuery)) {
            return fromQuery;
          }
          if (prev && sorted.some((l) => l.code === prev)) return prev;
          const preferred =
            sorted.find((l) => l.isDefault)?.code ??
            sorted.find((l) => l.code === "en")?.code ??
            sorted[0]?.code ??
            "";
          return preferred;
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err?.message ||
              t("pro.system.localization.definitions.error.loadLanguages"),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, initialLang, t]);

  const ensureLanguageModules = React.useCallback(
    async (langCode: string) => {
      if (!token || !langCode) return;
      setDrafts((prev) => {
        const lang = { ...(prev[langCode] ?? {}) };
        let changed = false;
        for (const ctx of DEFINITION_CONTEXTS) {
          if (!lang[ctx] || (!lang[ctx]!.loaded && !lang[ctx]!.loading)) {
            lang[ctx] = { ...emptyModuleDraft(), loading: true };
            changed = true;
          }
        }
        if (!changed) return prev;
        return { ...prev, [langCode]: lang };
      });

      try {
        const modules = await localizationApi.fetchModulesForLanguage(
          token,
          langCode,
        );
        const byContext = new Map(
          modules.map((m) => [m.context as LocalizationContext, m]),
        );
        setDrafts((prev) => {
          const existing = prev[langCode] ?? {};
          const next: Partial<Record<LocalizationContext, ModuleDraft>> = {
            ...existing,
          };
          for (const ctx of DEFINITION_CONTEXTS) {
            const current = existing[ctx];
            // Preserve in-progress edits if already loaded
            if (current?.loaded) {
              next[ctx] = current;
              continue;
            }
            const mod = byContext.get(ctx);
            next[ctx] = {
              loaded: true,
              loading: false,
              version: mod?.version ?? 1,
              entries: entriesFromDefinitions(mod?.definitions ?? {}),
              error: null,
            };
          }
          return { ...prev, [langCode]: next };
        });
      } catch (err: any) {
        setDrafts((prev) => {
          const existing = prev[langCode] ?? {};
          const next: Partial<Record<LocalizationContext, ModuleDraft>> = {
            ...existing,
          };
          for (const ctx of DEFINITION_CONTEXTS) {
            if (existing[ctx]?.loaded) continue;
            next[ctx] = {
              ...emptyModuleDraft(),
              loaded: true,
              loading: false,
              error:
                err?.message ||
                t("pro.system.localization.definitions.error.loadModule"),
            };
          }
          return { ...prev, [langCode]: next };
        });
      }
    },
    [token, t],
  );

  React.useEffect(() => {
    if (activeLang) void ensureLanguageModules(activeLang);
  }, [activeLang, ensureLanguageModules]);

  // Keep the default language loaded as the source of truth for missing-key diffs.
  React.useEffect(() => {
    if (
      defaultLangCode &&
      activeLang &&
      defaultLangCode !== activeLang
    ) {
      void ensureLanguageModules(defaultLangCode);
    }
  }, [defaultLangCode, activeLang, ensureLanguageModules]);

  const refreshOrphanCount = React.useCallback(async () => {
    if (!token) return;
    try {
      const data = await localizationApi.fetchContentOrphans(token);
      setOrphanedCount(data.count);
    } catch {
      setOrphanedCount(0);
    }
  }, [token]);

  React.useEffect(() => {
    void refreshOrphanCount();
  }, [refreshOrphanCount]);

  // Leave guards
  React.useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDirty]);

  const removeOrphanedKeys = React.useCallback(async () => {
    if (!token || orphanedCount <= 0 || removingOrphans) return;
    const ok = await confirm(
      t("pro.system.localization.definitions.confirm.removeOrphansTitle"),
      t("pro.system.localization.definitions.confirm.removeOrphansBody", {
        count: orphanedCount,
      }),
      t("pro.system.localization.definitions.confirm.removeOrphans"),
      "danger",
    );
    if (!ok) return;
    setRemovingOrphans(true);
    setSaveError(null);
    try {
      const result = await localizationApi.deleteContentOrphans(token);
      const orphanSet = new Set(result.keys);
      setOrphanedCount(0);
      if (orphanSet.size > 0) {
        setDrafts((prev) => {
          let changed = false;
          const next: DefinitionsDraftState = { ...prev };
          for (const [langCode, langDraft] of Object.entries(prev)) {
            const cert = langDraft.certification;
            if (!cert?.loaded) continue;
            const filtered = cert.entries.filter((e) => {
              const key = e.key.trim();
              const baseline = e.baselineKey?.trim() ?? "";
              return !orphanSet.has(key) && !orphanSet.has(baseline);
            });
            if (filtered.length === cert.entries.length) continue;
            changed = true;
            next[langCode] = {
              ...langDraft,
              certification: { ...cert, entries: filtered },
            };
          }
          return changed ? next : prev;
        });
      }
    } catch (err: any) {
      setSaveError(
        err?.message ||
          t("pro.system.localization.definitions.error.removeOrphans"),
      );
      void refreshOrphanCount();
    } finally {
      setRemovingOrphans(false);
    }
  }, [
    token,
    orphanedCount,
    removingOrphans,
    confirm,
    t,
    refreshOrphanCount,
  ]);

  const safeNavigateBack = async () => {
    if (hasDirty) {
      const ok = await confirm(
        t("pro.system.localization.definitions.confirm.leaveTitle"),
        t("pro.system.localization.definitions.confirm.leaveBody"),
        t("pro.system.localization.definitions.confirm.leave"),
        "danger",
      );
      if (!ok) return;
    }
    navigate({ to: "/system/localization" });
  };

  const updateEntry = React.useCallback(
    (
      lang: string,
      context: LocalizationContext,
      entryId: string,
      patch: Partial<Pick<DraftEntry, "key" | "value">>,
    ) => {
      setDrafts((prev) => {
        const mod = prev[lang]?.[context];
        if (!mod) return prev;
        return {
          ...prev,
          [lang]: {
            ...prev[lang],
            [context]: {
              ...mod,
              entries: mod.entries.map((e) =>
                e.id === entryId ? { ...e, ...patch } : e,
              ),
            },
          },
        };
      });
    },
    [],
  );

  const toggleDelete = React.useCallback(
    (lang: string, context: LocalizationContext, entryId: string) => {
      setDrafts((prev) => {
        const mod = prev[lang]?.[context];
        if (!mod) return prev;
        const entry = mod.entries.find((e) => e.id === entryId);
        if (!entry) return prev;
        // New rows: remove entirely
        if (entry.baselineKey == null && !entry.deleted) {
          return {
            ...prev,
            [lang]: {
              ...prev[lang],
              [context]: {
                ...mod,
                entries: mod.entries.filter((e) => e.id !== entryId),
              },
            },
          };
        }
        return {
          ...prev,
          [lang]: {
            ...prev[lang],
            [context]: {
              ...mod,
              entries: mod.entries.map((e) =>
                e.id === entryId ? { ...e, deleted: !e.deleted } : e,
              ),
            },
          },
        };
      });
    },
    [],
  );

  const resetEntry = React.useCallback(
    (lang: string, context: LocalizationContext, entryId: string) => {
      setDrafts((prev) => {
        const mod = prev[lang]?.[context];
        if (!mod) return prev;
        const entry = mod.entries.find((e) => e.id === entryId);
        if (!entry || !isEntryDirty(entry)) return prev;
        // New rows: discard entirely
        if (entry.baselineKey == null) {
          return {
            ...prev,
            [lang]: {
              ...prev[lang],
              [context]: {
                ...mod,
                entries: mod.entries.filter((e) => e.id !== entryId),
              },
            },
          };
        }
        return {
          ...prev,
          [lang]: {
            ...prev[lang],
            [context]: {
              ...mod,
              entries: mod.entries.map((e) =>
                e.id === entryId
                  ? {
                      ...e,
                      key: e.baselineKey!,
                      value: e.baselineValue,
                      deleted: false,
                    }
                  : e,
              ),
            },
          },
        };
      });
    },
    [],
  );

  const resetModule = React.useCallback(
    (lang: string, context: LocalizationContext) => {
      setDrafts((prev) => {
        const mod = prev[lang]?.[context];
        if (!mod || countModuleDirty(mod) === 0) return prev;
        const entries = mod.entries
          .filter((e) => e.baselineKey != null)
          .map((e) =>
            isEntryDirty(e)
              ? {
                  ...e,
                  key: e.baselineKey!,
                  value: e.baselineValue,
                  deleted: false,
                }
              : e,
          );
        return {
          ...prev,
          [lang]: {
            ...prev[lang],
            [context]: { ...mod, entries },
          },
        };
      });
    },
    [],
  );

  const resetAllDirty = () => {
    setDrafts((prev) => {
      let changed = false;
      const next: DefinitionsDraftState = {};
      for (const [lang, langDraft] of Object.entries(prev)) {
        const nextLang: Partial<Record<LocalizationContext, ModuleDraft>> = {};
        for (const ctx of DEFINITION_CONTEXTS) {
          const mod = langDraft?.[ctx];
          if (!mod) continue;
          const entries = mod.entries
            .filter((e) => e.baselineKey != null)
            .map((e) => {
              if (!isEntryDirty(e)) return e;
              changed = true;
              return {
                ...e,
                key: e.baselineKey!,
                value: e.baselineValue,
                deleted: false,
              };
            });
          const removedNew = mod.entries.some(
            (e) => e.baselineKey == null && isEntryDirty(e),
          );
          if (removedNew) changed = true;
          nextLang[ctx] = { ...mod, entries };
        }
        next[lang] = nextLang;
      }
      return changed ? next : prev;
    });
  };

  const addString = React.useCallback(
    (lang: string, context: LocalizationContext) => {
      setDrafts((prev) => {
        const mod = prev[lang]?.[context] ?? {
          ...emptyModuleDraft(),
          loaded: true,
        };
        return {
          ...prev,
          [lang]: {
            ...prev[lang],
            [context]: {
              ...mod,
              loaded: true,
              loading: false,
              entries: [...mod.entries, newDraftEntry()],
            },
          },
        };
      });
    },
    [],
  );

  const moduleHandlers = React.useMemo(() => {
    if (!activeLang) return null;
    const handlers = {} as Record<
      LocalizationContext,
      {
        onUpdateEntry: (
          entryId: string,
          patch: Partial<Pick<DraftEntry, "key" | "value">>,
        ) => void;
        onToggleDelete: (entryId: string) => void;
        onResetEntry: (entryId: string) => void;
        onResetSection: () => void;
        onAddString: () => void;
        onToggleExpanded: () => void;
      }
    >;
    for (const ctx of DEFINITION_CONTEXTS) {
      const sectionId = sectionIdForContext(ctx);
      handlers[ctx] = {
        onUpdateEntry: (id, patch) => updateEntry(activeLang, ctx, id, patch),
        onToggleDelete: (id) => toggleDelete(activeLang, ctx, id),
        onResetEntry: (id) => resetEntry(activeLang, ctx, id),
        onResetSection: () => resetModule(activeLang, ctx),
        onAddString: () => addString(activeLang, ctx),
        onToggleExpanded: () => toggleSection(sectionId),
      };
    }
    return handlers;
  }, [
    activeLang,
    updateEntry,
    toggleDelete,
    resetEntry,
    resetModule,
    addString,
    toggleSection,
  ]);

  const changeLines = React.useMemo(() => {
    const lines: string[] = [];
    for (const [lang, langDraft] of Object.entries(drafts)) {
      for (const ctx of DEFINITION_CONTEXTS) {
        const mod = langDraft?.[ctx];
        if (!mod) continue;
        for (const entry of mod.entries) {
          if (!isEntryDirty(entry)) continue;
          if (entry.baselineKey == null) {
            lines.push(
              `${lang}/${ctx}: + ${entry.key || t("pro.system.localization.definitions.emptyKey")}`,
            );
          } else if (entry.deleted) {
            lines.push(`${lang}/${ctx}: − ${entry.baselineKey}`);
          } else {
            lines.push(`${lang}/${ctx}: ✎ ${entry.key}`);
          }
          if (lines.length >= 12) {
            lines.push("…");
            return lines;
          }
        }
      }
    }
    return lines;
  }, [drafts, t]);

  const floatingActions = React.useMemo((): FloatingAction[] => {
    if (!hasDirty) return [];
    return [
      {
        id: "reset",
        icon: <RotateCcw size={16} />,
        shape: "circle",
        tooltip: t("pro.system.localization.definitions.resetAll"),
        disabled: saving,
        onClick: resetAllDirty,
      },
      {
        id: "save",
        label: saving
          ? t("pro.system.localization.definitions.saving")
          : t("pro.system.localization.definitions.save", { count: totalDirty }),
        icon: <Save size={16} />,
        shape: "rectangle",
        loading: saving,
        disabled: saving,
        tooltip:
          changeLines.length > 0 ? (
            <div>
              {changeLines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          ) : undefined,
        onClick: () => void handleSave(),
      },
    ];
  }, [hasDirty, totalDirty, saving, changeLines, t, resetAllDirty]);

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Validate duplicates per module
      for (const [lang, langDraft] of Object.entries(drafts)) {
        for (const ctx of DEFINITION_CONTEXTS) {
          const mod = langDraft?.[ctx];
          if (!mod || countModuleDirty(mod) === 0) continue;
          const dupes = findDuplicateKeys(mod.entries);
          if (dupes.length) {
            throw new Error(
              t("pro.system.localization.definitions.validation.duplicateKey", {
                lang,
                ctx,
                keys: dupes.join(", "),
              }),
            );
          }
          const emptyKey = mod.entries.some(
            (e) => !e.deleted && !e.key.trim() && (e.value.trim() || e.baselineKey == null),
          );
          if (emptyKey) {
            throw new Error(
              t("pro.system.localization.definitions.validation.emptyKey", {
                lang,
                ctx,
              }),
            );
          }
        }
      }

      for (const [lang, langDraft] of Object.entries(drafts)) {
        for (const ctx of DEFINITION_CONTEXTS) {
          const mod = langDraft?.[ctx];
          if (!mod || countModuleDirty(mod) === 0) continue;
          const definitions = buildDefinitionsFromEntries(mod.entries);
          const result = await localizationApi.putLocalizationModule(
            token,
            ctx,
            lang,
            definitions,
          );
          setDrafts((prev) => ({
            ...prev,
            [lang]: {
              ...prev[lang],
              [ctx]: {
                loaded: true,
                loading: false,
                version: result.version,
                entries: entriesFromDefinitions(
                  result.definitions ?? definitions,
                ),
                error: null,
              },
            },
          }));
        }
      }
    } catch (err: any) {
      setSaveError(
        err?.message || t("pro.system.localization.definitions.error.save"),
      );
    } finally {
      setSaving(false);
    }
  };

  const activeDraft = drafts[activeLang];

  const quickFilterCounts = React.useMemo(() => {
    const counts = { all: 0, unsaved: 0, empty: 0 };
    if (!activeDraft) return counts;
    for (const ctx of DEFINITION_CONTEXTS) {
      const mod = activeDraft[ctx];
      if (!mod?.loaded) continue;
      for (const entry of mod.entries) {
        counts.all += 1;
        if (matchesQuickFilter(entry, "unsaved")) counts.unsaved += 1;
        if (matchesQuickFilter(entry, "empty")) counts.empty += 1;
      }
    }
    return counts;
  }, [activeDraft]);

  React.useEffect(() => {
    if (!activeDraft || !deferredSearchQuery.trim()) return;
    const matchingSectionIds = DEFINITION_CONTEXTS.filter((ctx) => {
      const sectionId = sectionIdForContext(ctx);
      if (isSectionExpanded(sectionId)) return false;
      const mod = activeDraft[ctx];
      if (!mod?.loaded) return false;
      return mod.entries.some(
        (entry) =>
          matchesQuickFilter(entry, quickFilter) && matchesSearch(entry, deferredSearchQuery),
      );
    }).map(sectionIdForContext);
    if (matchingSectionIds.length > 0) expandSections(matchingSectionIds);
  }, [activeDraft, quickFilter, deferredSearchQuery, isSectionExpanded, expandSections]);

  const sectionLayout = bindSectionLayout(
    DEFINITION_CONTEXTS.map((ctx) => ({
      id: sectionIdForContext(ctx),
      label: ctx,
      icon: contextIcon(ctx, 14),
    })),
  );

  const missingKeys = React.useMemo(() => {
    if (!activeLang || !defaultLangCode || activeLang === defaultLangCode) {
      return [];
    }
    return collectMissingKeys(drafts[defaultLangCode], drafts[activeLang]);
  }, [activeLang, defaultLangCode, drafts]);

  // Load catalog usedTKeys when editing the default language (for untranslated add).
  React.useEffect(() => {
    if (!token || !activeLang || !defaultLangCode || activeLang !== defaultLangCode) {
      return;
    }
    let cancelled = false;
    localizationApi
      .fetchLocalizationCatalog(token)
      .then((catalog) => {
        if (!cancelled) {
          setCatalogUsedTKeys(catalog.usedTKeys ?? []);
          setCatalogDefaultMessages(catalog.defaultMessages ?? {});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogUsedTKeys([]);
          setCatalogDefaultMessages({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, activeLang, defaultLangCode]);

  const untranslatedCatalogKeys = React.useMemo(() => {
    if (!activeLang || !defaultLangCode || activeLang !== defaultLangCode) {
      return [];
    }
    return collectUntranslatedCatalogKeys(
      catalogUsedTKeys,
      drafts[defaultLangCode],
      catalogDefaultMessages,
    );
  }, [activeLang, defaultLangCode, catalogUsedTKeys, catalogDefaultMessages, drafts]);

  const defaultValuesByContext = React.useMemo(() => {
    const out: Partial<Record<LocalizationContext, Record<string, string>>> = {};
    if (!defaultLangCode) return out;
    const source = drafts[defaultLangCode];
    if (!source) return out;
    for (const ctx of DEFINITION_CONTEXTS) {
      const mod = source[ctx];
      if (!mod?.loaded) continue;
      const map: Record<string, string> = {};
      for (const entry of mod.entries) {
        if (entry.deleted) continue;
        const key = entry.key.trim();
        if (key) map[key] = entry.value;
      }
      out[ctx] = map;
    }
    return out;
  }, [defaultLangCode, drafts]);

  /** Empty draft rows that have a default-language reference string to translate from. */
  const emptyFieldsForAi = React.useMemo((): AiFillEmptyField[] => {
    const out: AiFillEmptyField[] = [];
    if (!activeLang || !defaultLangCode || activeLang === defaultLangCode) {
      return out;
    }
    const langDraft = drafts[activeLang];
    if (!langDraft) return out;
    for (const ctx of DEFINITION_CONTEXTS) {
      const mod = langDraft[ctx];
      if (!mod?.loaded) continue;
      const refs = defaultValuesByContext[ctx] ?? {};
      for (const entry of mod.entries) {
        if (entry.deleted) continue;
        const key = entry.key.trim();
        if (!key || entry.value.trim()) continue;
        const sourceText = refs[key]?.trim();
        if (!sourceText) continue;
        out.push({
          context: ctx,
          entryId: entry.id,
          key,
          sourceText,
        });
      }
    }
    return out;
  }, [activeLang, defaultLangCode, drafts, defaultValuesByContext]);

  const sourceLanguageName = languages.find((l) => l.code === defaultLangCode)?.name;
  const targetLanguageName = languages.find((l) => l.code === activeLang)?.name;

  const emptyValueCount = React.useMemo(() => {
    if (!activeLang || activeLang === defaultLangCode) return 0;
    const langDraft = drafts[activeLang];
    if (!langDraft) return 0;
    let count = 0;
    for (const ctx of DEFINITION_CONTEXTS) {
      const mod = langDraft[ctx];
      if (!mod?.loaded) continue;
      for (const entry of mod.entries) {
        if (entry.deleted) continue;
        if (entry.key.trim() && !entry.value.trim()) count += 1;
      }
    }
    return count;
  }, [activeLang, defaultLangCode, drafts]);

  const addKeysToActiveDraft = React.useCallback(
    (
      items: Array<{
        context: LocalizationContext;
        key: string;
        value?: string;
      }>,
    ) => {
      if (!activeLang || items.length === 0) return;
      setDrafts((prev) => {
        const langDraft = { ...(prev[activeLang] ?? {}) };
        let changed = false;
        const byContext = new Map<LocalizationContext, typeof items>();
        for (const item of items) {
          const list = byContext.get(item.context) ?? [];
          list.push(item);
          byContext.set(item.context, list);
        }
        for (const [ctx, ctxItems] of byContext) {
          const mod = langDraft[ctx] ?? { ...emptyModuleDraft(), loaded: true };
          const existingKeys = new Set(
            mod.entries.filter((e) => !e.deleted).map((e) => e.key.trim()),
          );
          const additions: DraftEntry[] = [];
          for (const { key, value } of ctxItems) {
            if (existingKeys.has(key)) continue;
            existingKeys.add(key);
            additions.push({
              ...newDraftEntry(),
              key,
              value: value ?? "",
            });
          }
          if (!additions.length) continue;
          changed = true;
          langDraft[ctx] = {
            ...mod,
            loaded: true,
            loading: false,
            entries: [...mod.entries, ...additions],
          };
        }
        if (!changed) return prev;
        return { ...prev, [activeLang]: langDraft };
      });
    },
    [activeLang],
  );

  const addMissingKeysToDraft = React.useCallback(() => {
    // Non-default languages: add keys with empty values so AI-fill / manual
    // translate can run; English source stays available as the focus hint.
    addKeysToActiveDraft(
      missingKeys.map(({ context, key }) => ({ context, key })),
    );
  }, [addKeysToActiveDraft, missingKeys]);

  const addUntranslatedKeysToDraft = React.useCallback(() => {
    addKeysToActiveDraft(untranslatedCatalogKeys);
  }, [addKeysToActiveDraft, untranslatedCatalogKeys]);

  const applyAiFillUpdates = (updates: Map<string, string>) => {
    if (!activeLang || updates.size === 0) return;
    setDrafts((prev) => {
      const langDraft = prev[activeLang];
      if (!langDraft) return prev;
      const next: Partial<Record<LocalizationContext, ModuleDraft>> = {
        ...langDraft,
      };
      for (const ctx of DEFINITION_CONTEXTS) {
        const mod = langDraft[ctx];
        if (!mod) continue;
        let changed = false;
        const entries = mod.entries.map((e) => {
          const text = updates.get(e.id);
          if (text == null) return e;
          changed = true;
          return { ...e, value: text };
        });
        if (changed) next[ctx] = { ...mod, entries };
      }
      return { ...prev, [activeLang]: next };
    });
  };

  const pageContextActions = React.useMemo((): ContextAction[] => {
    const actions: ContextAction[] = [];
    if (missingKeys.length > 0) {
      actions.push({
        id: "add-missing",
        label: t("pro.system.localization.definitions.addMissing", {
          count: missingKeys.length,
        }),
        onClick: addMissingKeysToDraft,
      });
    }
    if (untranslatedCatalogKeys.length > 0) {
      actions.push({
        id: "add-untranslated",
        label: t("pro.system.localization.definitions.addUntranslated", {
          count: untranslatedCatalogKeys.length,
        }),
        onClick: addUntranslatedKeysToDraft,
      });
    }
    if (orphanedCount > 0) {
      actions.push({
        id: "remove-orphans",
        label: t("pro.system.localization.definitions.removeOrphans", {
          count: orphanedCount,
        }),
        icon: <Trash2 size={14} />,
        color: "danger",
        disabled: removingOrphans,
        onClick: () => void removeOrphanedKeys(),
      });
    }
    if (emptyValueCount > 0) {
      actions.push({
        id: "ai-fill",
        label:
          emptyFieldsForAi.length > 0
            ? `${t("pro.system.localization.definitions.aiFill")} (${emptyFieldsForAi.length})`
            : t("pro.system.localization.definitions.aiFill"),
        icon: <Sparkles size={14} />,
        color: "primary",
        disabled: emptyFieldsForAi.length === 0,
        onClick: () => setAiFillOpen(true),
      });
    }
    return actions;
  }, [
    missingKeys.length,
    untranslatedCatalogKeys.length,
    orphanedCount,
    removingOrphans,
    emptyValueCount,
    emptyFieldsForAi.length,
    t,
    addMissingKeysToDraft,
    addUntranslatedKeysToDraft,
    removeOrphanedKeys,
  ]);

  if (!token) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <Callout variant="error">
            {t("pro.system.localization.signInRequired")}
          </Callout>
        </div>
      </div>
    );
  }

  return (
    <>
      <DetailPageLayout
        backLink={{
          label: t("pro.system.localization.title"),
          onClick: () => void safeNavigateBack(),
        }}
        header={{
          media: <BookText size={28} color={vars.color.fgMuted} />,
          title: t("pro.system.localization.definitions"),
        }}
        tabs={
          languages.length > 0 ? (
            <TabSwitcher
              aria-label={t("pro.system.localization.definitions.aria.languages")}
              tabs={languages.map((lang) => ({
                value: lang.code,
                label: lang.name,
                icon: lang.flagUrl ? (
                  <img src={lang.flagUrl} alt="" className={s.flagIcon} />
                ) : undefined,
              }))}
              value={activeLang}
              onChange={(code) => {
                setActiveLang(code);
                setAiFillOpen(false);
                void navigate({
                  to: "/system/localization/definitions",
                  search: { lang: code },
                  replace: true,
                });
              }}
            />
          ) : undefined
        }
        contextActions={pageContextActions.length > 0 ? pageContextActions : undefined}
        {...sectionLayout}
        floatingActions={floatingActions}
      >
        {loadError && (
          <div style={{ marginBottom: 16 }}>
            <Callout variant="error">{loadError}</Callout>
          </div>
        )}
        {saveError && (
          <div style={{ marginBottom: 16 }}>
            <Callout variant="error">{saveError}</Callout>
          </div>
        )}

        {!activeLang && !loadError && (
          <Text level="body-small" style={{ color: vars.color.fgMuted }}>
            {t("pro.system.localization.definitions.loadingLanguages")}
          </Text>
        )}

        {activeLang && activeDraft && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                type="button"
                size="sm"
                color={quickFilter === "all" ? "primary" : "neutral"}
                variant={quickFilter === "all" ? "solid" : "outline"}
                onClick={() => setQuickFilter("all")}
              >
                {t("pro.system.localization.definitions.quickFilter.all")} ({quickFilterCounts.all})
              </Button>
              <Button
                type="button"
                size="sm"
                color={quickFilter === "unsaved" ? "primary" : "neutral"}
                variant={quickFilter === "unsaved" ? "solid" : "outline"}
                onClick={() => setQuickFilter("unsaved")}
              >
                {t("pro.system.localization.definitions.quickFilter.unsaved")} ({quickFilterCounts.unsaved})
              </Button>
              <Button
                type="button"
                size="sm"
                color={quickFilter === "empty" ? "primary" : "neutral"}
                variant={quickFilter === "empty" ? "solid" : "outline"}
                onClick={() => setQuickFilter("empty")}
              >
                {t("pro.system.localization.definitions.quickFilter.empty")} ({quickFilterCounts.empty})
              </Button>
            </div>
            <div
              className={searchWrap}
              data-expanded={
                searchFocused || searchQuery.trim().length > 0 ? "true" : undefined
              }
              onClick={() => searchInputRef.current?.focus()}
            >
              <svg
                className={searchIcon}
                width={14}
                height={14}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                ref={searchInputRef}
                className={searchInput}
                placeholder={t("pro.system.localization.definitions.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                aria-label={t("pro.system.localization.definitions.searchPlaceholder")}
              />
              {searchQuery.trim() ? (
                <button
                  type="button"
                  className={searchClearBtn}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  aria-label={t("shared.filter.clearSearch")}
                  title={t("shared.filter.clearSearch")}
                >
                  <svg
                    width={14}
                    height={14}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              ) : null}
            </div>
          </div>
        )}

        {activeLang &&
          moduleHandlers &&
          DEFINITION_CONTEXTS.map((ctx) => {
            const handlers = moduleHandlers[ctx];
            return (
              <ModuleSection
                key={`${activeLang}-${ctx}`}
                context={ctx}
                draft={activeDraft?.[ctx] ?? { ...emptyModuleDraft(), loading: true }}
                referenceValues={
                  activeLang !== defaultLangCode
                    ? defaultValuesByContext[ctx]
                    : undefined
                }
                onUpdateEntry={handlers.onUpdateEntry}
                onToggleDelete={handlers.onToggleDelete}
                onResetEntry={handlers.onResetEntry}
                onResetSection={handlers.onResetSection}
                onAddString={handlers.onAddString}
                expanded={isSectionExpanded(sectionIdForContext(ctx))}
                onToggleExpanded={handlers.onToggleExpanded}
                quickFilter={quickFilter}
                searchQuery={deferredSearchQuery}
              />
            );
          })}
      </DetailPageLayout>

      <AiFillAssistModal
        open={aiFillOpen}
        onClose={() => setAiFillOpen(false)}
        accessToken={token}
        sourceLanguage={defaultLangCode || "en"}
        sourceLanguageName={sourceLanguageName}
        targetLanguage={activeLang}
        targetLanguageName={targetLanguageName}
        emptyFields={emptyFieldsForAi}
        onApply={applyAiFillUpdates}
      />

      <ConfirmModal />
    </>
  );
}
